import * as THREE from 'three';
import { createFloorMaterial, createWallMaterial } from '../utils/materials';
import { Freezer, FreezerData } from './Freezer';

const ROOM_WIDTH = 10;
const ROOM_DEPTH = 8;
const ROOM_HEIGHT = 3;
const FREEZER_SPACING = 1.8;

export class FreezerRoom {
  public scene: THREE.Scene;
  public freezers: Freezer[] = [];

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.buildRoom();
    this.buildLighting();
  }

  private buildRoom(): void {
    // Floor
    const floorGeo = new THREE.PlaneGeometry(ROOM_WIDTH, ROOM_DEPTH);
    const floor = new THREE.Mesh(floorGeo, createFloorMaterial());
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    this.scene.add(floor);

    // Back wall
    const wallMat = createWallMaterial();
    const backWall = new THREE.Mesh(
      new THREE.PlaneGeometry(ROOM_WIDTH, ROOM_HEIGHT),
      wallMat
    );
    backWall.position.set(0, ROOM_HEIGHT / 2, -ROOM_DEPTH / 2);
    backWall.receiveShadow = true;
    this.scene.add(backWall);

    // Left wall
    const leftWall = new THREE.Mesh(
      new THREE.PlaneGeometry(ROOM_DEPTH, ROOM_HEIGHT),
      wallMat
    );
    leftWall.rotation.y = Math.PI / 2;
    leftWall.position.set(-ROOM_WIDTH / 2, ROOM_HEIGHT / 2, 0);
    this.scene.add(leftWall);

    // Right wall
    const rightWall = new THREE.Mesh(
      new THREE.PlaneGeometry(ROOM_DEPTH, ROOM_HEIGHT),
      wallMat
    );
    rightWall.rotation.y = -Math.PI / 2;
    rightWall.position.set(ROOM_WIDTH / 2, ROOM_HEIGHT / 2, 0);
    this.scene.add(rightWall);

    // Ceiling
    const ceiling = new THREE.Mesh(
      new THREE.PlaneGeometry(ROOM_WIDTH, ROOM_DEPTH),
      new THREE.MeshStandardMaterial({ color: 0xe8edf2, metalness: 0, roughness: 1 })
    );
    ceiling.rotation.x = Math.PI / 2;
    ceiling.position.y = ROOM_HEIGHT;
    this.scene.add(ceiling);
  }

  private buildLighting(): void {
    // Ambient - bright lab
    const ambient = new THREE.AmbientLight(0xeef2f7, 0.7);
    this.scene.add(ambient);

    // Main directional light - hűtők mögül, árnyék a néző felé
    const dirLight = new THREE.DirectionalLight(0xffffff, 1.0);
    dirLight.position.set(1, 5, -3);
    dirLight.castShadow = true;
    dirLight.shadow.mapSize.width = 2048;
    dirLight.shadow.mapSize.height = 2048;
    dirLight.shadow.camera.near = 0.5;
    dirLight.shadow.camera.far = 15;
    dirLight.shadow.camera.left = -6;
    dirLight.shadow.camera.right = 6;
    dirLight.shadow.camera.top = 6;
    dirLight.shadow.camera.bottom = -6;
    this.scene.add(dirLight);

    // Ceiling spot lights - hűtők felett, hátsó falhoz közel
    const spotPositions = [
      [-2.5, ROOM_HEIGHT - 0.1, -2.5],
      [0, ROOM_HEIGHT - 0.1, -2.5],
      [2.5, ROOM_HEIGHT - 0.1, -2.5],
    ];
    for (const [x, y, z] of spotPositions) {
      const spot = new THREE.SpotLight(0xffffff, 1.2, 8, Math.PI / 4, 0.4);
      spot.position.set(x, y, z);
      spot.target.position.set(x, 0, z);
      spot.castShadow = true;
      spot.shadow.mapSize.width = 1024;
      spot.shadow.mapSize.height = 1024;
      this.scene.add(spot);
      this.scene.add(spot.target);
    }

    // Subtle fill light
    const rimLight = new THREE.PointLight(0xc8d8f0, 0.5, 12);
    rimLight.position.set(0, 2.5, -ROOM_DEPTH / 2 + 0.5);
    this.scene.add(rimLight);
  }

  loadFreezers(freezerDataList: FreezerData[]): void {
    const totalWidth = (freezerDataList.length - 1) * FREEZER_SPACING;
    const startX = -totalWidth / 2;

    for (let i = 0; i < freezerDataList.length; i++) {
      const freezer = new Freezer(freezerDataList[i]);
      freezer.mesh.position.set(
        startX + i * FREEZER_SPACING,
        0,
        -ROOM_DEPTH / 2 + 1.2
      );
      this.scene.add(freezer.mesh);
      this.freezers.push(freezer);
    }
  }

  getFreezerById(id: string): Freezer | undefined {
    return this.freezers.find((f) => f.freezerData.id === id);
  }

  dimAllExcept(freezerIds: string[]): void {
    for (const freezer of this.freezers) {
      const shouldDim = freezerIds.length > 0 && !freezerIds.includes(freezer.freezerData.id);
      freezer.dim(shouldDim);
    }
  }

  resetDimming(): void {
    for (const freezer of this.freezers) {
      freezer.dim(false);
    }
  }

  update(time: number, delta: number): void {
    for (const freezer of this.freezers) {
      freezer.update(time, delta);
    }
  }
}
